import { ClockCircleFilled, CopyOutlined } from '@ant-design/icons'
import { statusClassDict, translateStatus } from '@constants/orderDispatch'
import { TripStatusIcon } from '@constants/TripStatusIcons'
import { BBDTag, GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { getCarrierDeliveryWindow } from '@utils/general'
import { Col, message, Tooltip } from 'antd'

import { formatTimeWindow } from '../../../../../_SiteWide/OrderDisplay/OrderTimeFormats'
import { useWizardMaster } from '../../../../Contexts/WizardMasterContext'

export function getDeliveryWindow(order: Gravitate.Order) {
  if (!order) return null
  const hasCarrier = order.carrier || order.manager
  const windowStart = hasCarrier ? order.carrier_window_start : order.dispatch_window_start
  const windowEnd = hasCarrier ? order.carrier_window_end : order.dispatch_window_end
  return hasCarrier
    ? getCarrierDeliveryWindow(order)
    : formatTimeWindow(windowStart ?? '', windowEnd ?? '', false, false, order.dispatch_window_start_local)
}

type PaneHeaderProps = {
  title?: string
  children?: React.ReactNode
  showStatus?: boolean
}

export function PaneHeader({ title, children, showStatus }: PaneHeaderProps) {
  const { wizardOrder, isBackhaul } = useWizardMaster()
  const status: string = !showStatus ? 'edit' : wizardOrder?.trip_status
  const hasNoStatus = status === 'unset' || !status
  const headerClass = hasNoStatus ? 'scheduled' : statusClassDict[status]
  const window = getDeliveryWindow(wizardOrder)
  const showDeliveryWindow = Boolean(wizardOrder?.id)

  return (
    <div className={`pane-header ${headerClass}`} data-cy={headerClass}>
      <Horizontal justifyContent='space-between' verticalCenter gap={8}>
        <OrderNumberHeader order={wizardOrder} />
        {showStatus && (
          <BBDTag
            className='order-status-card px-2 py-1'
            icon={hasNoStatus ? <ClockCircleFilled /> : <TripStatusIcon status={status} />}
            data-cy='order-status-card'
          >
            <Texto category='h5'>{hasNoStatus ? 'Not Started' : translateStatus[status]}</Texto>
          </BBDTag>
        )}
      </Horizontal>
      <Horizontal>
        <Vertical justifyContent='flex-end'>
          {!isBackhaul && showDeliveryWindow && (
            <Horizontal>
              <Texto>Delivery Window: {window}</Texto>
            </Horizontal>
          )}
        </Vertical>
      </Horizontal>
      {title && (
        <Texto category='label' className='mt-1'>
          {title}
        </Texto>
      )}
      {children}
    </div>
  )
}

export function OrderNumberHeader({ order }: { order: Gravitate.Order }) {
  const [messageApi, contextHolder] = message.useMessage()

  const handleCopy = () => {
    const url = `${window.location.origin}/order_overview_page/all?order_number=${order?.number}`
    navigator.clipboard.writeText(url)
    messageApi.open({
      type: 'success',
      content: 'Order Link Copied',
    })
  }

  return (
    <Horizontal verticalCenter gap={8}>
      {contextHolder}
      <Texto category='h2' weight='bold'>
        Order {order?.number} {order?.po ? `(PO ${order?.po})` : ''}
      </Texto>
      <Tooltip title='Copy Order Link'>
        <GraviButton shape='circle' size='small' icon={<CopyOutlined />} onClick={handleCopy} />
      </Tooltip>
    </Horizontal>
  )
}
